import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { AddIcon, ContactPersonsIcon } from '@common/utils/icons/icons';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectIsUserAdminOrHigher } from '../../../store/general/general-slice-selectors';
import UserForm from '../../user/UserForm';
import UsersTable from '../../user/UsersTable';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  selectOrganizationsData,
  selectOrganizationUsersData,
} from '../../../store/organization/organization-slice-selectors';
import {
  resetOrganizationsRestState,
  resetSearchRestStatus,
  resetUsersRestState,
} from '../../../store/organization/organization-slice';
import Tooltip from '@mui/material/Tooltip';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import TextInput from '@common/components/Inputs/TextInput';
import SearchType from '../../../models/enums/search';
import SearchBar from '../../ui/SearchBar';
import SearchResult from '../../../models/misc/search-result';
import {
  getOrganizationUsersById,
  patchOrganizationById,
  patchUserById,
} from '../../../store/organization/organization-actions';
import { isValidationResultArray } from '@common/interfaces/api/validation-result';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import { validateUserPhoneNumber } from '../../../services/validation/user-validation';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import UserPatchType from '../../../models/enums/user-patch-type';
import Typography from '@mui/material/Typography';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const usersData = useAppSelector((state) => selectOrganizationUsersData(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));

  const [isAddUserFormOpen, setIsAddUserFormOpen] = useState(false);
  const [isContactPersonsFormOpen, setIsContactPersonsFormOpen] = useState(false);
  const [isPhoneNumberFormOpen, setIsPhoneNumberFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(null);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: organizationData.rest.patchError,
      resetPostHandler: () =>
        dispatch(
          resetOrganizationsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }),
        ),
      patchError: usersData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  const openAddUserFormClickHandler = () => {
    setIsAddUserFormOpen(true);
  };

  const closeAddUserFormHandler = () => {
    setIsAddUserFormOpen(false);
  };

  const openAddContactFormClickHandler = () => {
    setIsContactPersonsFormOpen(true);
  };

  const closeAddContactFormHandler = () => {
    setIsContactPersonsFormOpen(false);
    setSelectedUser(null);
    setIsPhoneNumberFormOpen(false);
  };

  const closeContactPersonsFormHandler = useCallback(() => {
    setIsContactPersonsFormOpen(false);
    setSelectedUser(null);
    setIsPhoneNumberFormOpen(false);
    dispatch(resetSearchRestStatus());
    dispatch(resetOrganizationsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
    dispatch(resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  }, [dispatch]);

  useEffect(() => {
    if (usersData.rest.patchStatus === HttpFetchStatus.Success && selectedUser !== null) {
      addUserHandler(selectedUser);
    }
  });

  useEffect(() => {
    if (organizationData.current?.id == null) return;
    if (organizationData.rest.patchStatus === HttpFetchStatus.Success) {
      closeContactPersonsFormHandler();
      dispatch(getOrganizationUsersById({ org: organizationData.current.id }));
    } else if (
      organizationData.rest.patchStatus === HttpFetchStatus.Failed &&
      isValidationResultArray(organizationData.rest.patchError) &&
      organizationData.rest.patchError.some((e) => e.inputKey === 'contactIds')
    ) {
      setIsContactPersonsFormOpen(true);
      setIsPhoneNumberFormOpen(true);
    }
  }, [
    closeContactPersonsFormHandler,
    dispatch,
    organizationData,
    organizationData.rest.patchStatus,
  ]);

  const closeHandler = () => {
    dispatch(
      resetUsersRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        patchId: null,
        patchType: null,
      }),
    );
  };

  const addUserHandler = (result: SearchResult) => {
    if (organizationData.current?.id != null) {
      setSelectedUser(result);
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { contactIds: [...(organizationData.current?.contactIds ?? []), result.id] },
        }),
      );
    }
  };

  const setSelectedUserHandler = (id: string, name: string) => {
    setSelectedUser({ id, name: name, type: SearchType.USER, role: null });
  };

  const saveUserClickHandler = () => {
    if (selectedUser != null) {
      dispatch(
        patchUserById({
          id: selectedUser.id,
          patchType: UserPatchType.GeneralPatch,
          data: { phoneNumber },
        }),
      );
    }
  };

  const {
    value: phoneNumber,
    validationResult: phoneNumberValidationResult,
    valueChangeHandler: phoneNumberChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateUserPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('userIds'),
    removeServerError,
    resetToggle,
  );

  const organizationUsers = usersData.data?.filter(
    (user) => user.organization.id === organizationData.current?.id,
  );
  const userLimitReached =
    organizationData.current?.userLimit != null &&
    (organizationUsers ?? []).length >= organizationData.current.userLimit;

  return (
    <React.Fragment>
      <Stack spacing={2} p={2}>
        <Typography variant="h2" fontSize={24} lineHeight={2}>
          {`${organizationData.current?.name ?? ''} - ${t('button-users')}`}
        </Typography>
        {isAdmin && (
          <Stack direction="row" spacing={2}>
            <Tooltip title={userLimitReached ? t('info-tooltip-limit-reached') : undefined}>
              <span>
                <Button
                  startIcon={<AddIcon />}
                  onClick={openAddUserFormClickHandler}
                  disabled={userLimitReached}
                >
                  {t('button-add-user')}
                </Button>
              </span>
            </Tooltip>
            <Button startIcon={<ContactPersonsIcon />} onClick={openAddContactFormClickHandler}>
              {t('button-add-contact-person')}
            </Button>
          </Stack>
        )}

        {organizationData.current != null && <UsersTable onUserSelect={setSelectedUserHandler} />}
      </Stack>

      <UserForm open={isAddUserFormOpen} onClose={closeAddUserFormHandler} />
      {isContactPersonsFormOpen && (
        <Dialog
          open={isContactPersonsFormOpen}
          onClose={closeAddContactFormHandler}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {isPhoneNumberFormOpen ? selectedUser?.name : t('title-robot-contact-people')}
          </DialogTitle>
          <DialogContent>
            {usersData.rest.patchError !== null && isHttpError(usersData.rest.patchError) && (
              <CustomAlert severity="error" header={t(usersData.rest.patchError.message)} />
            )}
            <Box mt={2}>
              {isPhoneNumberFormOpen ? (
                <TextInput
                  value={phoneNumber}
                  onChange={phoneNumberChangeHandler}
                  validationResult={phoneNumberValidationResult}
                  label={t('input-label-user-phone-number')}
                />
              ) : (
                <SearchBar
                  topLabel={t('input-label-robot-contact-person')}
                  onItemClick={addUserHandler}
                  inline
                  fields={[SearchType.USER]}
                  fullSize
                  childOrgId={organizationData.current?.id}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeContactPersonsFormHandler}>{t('button-close')}</Button>
            {isPhoneNumberFormOpen && (
              <Button onClick={saveUserClickHandler} variant="contained" disabled={!phoneNumber}>
                {t('button-save')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
      <CustomSnackbar
        open={usersData.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-user')}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={usersData.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-user')}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default UsersPage;
